import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { observable, runInAction } from 'mobx';

/**
 * Plans a company has asked for with "Upgrade now" (LB-6187), remembered in this browser for a day, so a reload doesn't
 * offer the button again while the team is still handling the request. Kept as { "<company>|<tier>": time }.
 */
const STORAGE_KEY = 'livable_upgrade_requests';
const KEEP_MS = 24 * 60 * 60 * 1000;

// Autopilot includes everything in Plus, so asking for it answers a Plus lock too.
const RANK = {
  Plus: 1,
  Autopilot: 2
};

// Storage isn't observable: every note bumps this, so each lock on screen reads again at once.
const revision = observable.box(0);
const bump = () => runInAction(() => revision.set(revision.get() + 1));

// A request made in another tab of this browser shows here too, so it isn't offered, and sent, twice.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY || event.key === null) bump();
  });
}

// What this tab noted that storage refused (a private window, blocked site data).
let noted = {};

// localStorage holds the company id in lower case, and other screens pass it as the server wrote it.
const keyOf = (companyId, tier) => "".concat(String(companyId).toLowerCase(), "|").concat(tier);
const isKnownTier = tier => Object.prototype.hasOwnProperty.call(RANK, tier);
const stored = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_unused) {
    return {};
  }
};

// Storage and this tab's notes together, newest time per request, without anything older than a day.
const current = now => {
  const merged = {};
  [stored(), noted].forEach(entries => Object.entries(entries).forEach(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      at = _ref2[1];
    if (typeof at === 'number' && now - at < KEEP_MS && !(merged[key] >= at)) merged[key] = at;
  }));
  return merged;
};

/** Whether this company asked for this plan, or a higher one, in the last day. Observed by the locks that read it. */
export const upgradeRequested = (companyId, tier) => {
  revision.get();
  if (!companyId || !isKnownTier(tier)) return false;
  const entries = current(Date.now());
  return Object.keys(RANK).some(asked => RANK[asked] >= RANK[tier] && !!entries[keyOf(companyId, asked)]);
};

/** Remembers that this company asked for this plan just now. */
export const noteUpgradeRequested = (companyId, tier) => {
  if (!companyId || !isKnownTier(tier)) return;
  const now = Date.now();
  const entries = _objectSpread(_objectSpread({}, current(now)), {}, {
    [keyOf(companyId, tier)]: now
  });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    noted = {};
  } catch (_unused2) {
    // Storage refused it: this tab still remembers it.
    noted = entries;
  }
  bump();
};